import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
} from 'lucide-react';

const CmsPages = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await api.get('/cms/pages/all');
      setPages(res.data);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    try {
      await api.delete(`/cms/pages/${pageId}`);
      setPages(pages.filter(p => p.id !== pageId));
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  const handleToggleStatus = async (page) => {
    const newStatus = page.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/cms/pages/${page.id}`, { status: newStatus });
      setPages(pages.map(p => p.id === page.id ? { ...p, status: newStatus } : p));
    } catch (error) {
      console.error('Failed to update page status:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="cms-pages">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#2a3e5d]">Pages</h1>
            <p className="text-sm text-[#1e293b]/60 mt-1">
              Create and manage custom pages for your site
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/pages/new')}
            className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
            data-testid="create-page-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Page
          </Button>
        </div>

        {pages.length === 0 ? (
          <Card className="border-[#f8fafc]">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-[#02d1d4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#1e293b] mb-2">No pages yet</h3>
              <p className="text-sm text-[#1e293b]/60 mb-6">
                Create your first page to get started — About Us, Contact, FAQs, and more.
              </p>
              <Button
                onClick={() => navigate('/admin/pages/new')}
                className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Page
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {pages.map((page) => (
              <Card
                key={page.id}
                className="border-[#f8fafc] hover:shadow-sm transition-shadow"
                data-testid={`page-row-${page.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#f8fafc] flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-[#02d1d4]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-[#1e293b] truncate">{page.title}</h3>
                      <Badge
                        className={
                          page.status === 'published'
                            ? 'bg-[#f8fafc] text-[#2a3e5d] border-[#2a3e5d]/20'
                            : 'bg-[#f8fafc] text-[#02d1d4] border-[#02d1d4]/20'
                        }
                        data-testid={`page-status-${page.id}`}
                      >
                        {page.status}
                      </Badge>
                      {page.show_in_navigation !== 'none' && (
                        <Badge className="bg-[#f8fafc] text-[#20acf6] border-[#20acf6]/20 text-xs">
                          {page.show_in_navigation}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#1e293b]/50 mt-0.5">/{page.slug}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleStatus(page)}
                      title={page.status === 'published' ? 'Unpublish' : 'Publish'}
                      data-testid={`toggle-status-${page.id}`}
                    >
                      {page.status === 'published' ? (
                        <Eye className="h-4 w-4 text-[#2a3e5d]" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-[#02d1d4]" />
                      )}
                    </Button>
                    {page.status === 'published' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        title="View page"
                      >
                        <Link to={`/page/${page.slug}`} target="_blank">
                          <ExternalLink className="h-4 w-4 text-[#20acf6]" />
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/admin/pages/${page.id}/edit`)}
                      title="Edit page"
                      data-testid={`edit-page-${page.id}`}
                    >
                      <Edit className="h-4 w-4 text-[#1e293b]/60" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(page.id)}
                      title="Delete page"
                      data-testid={`delete-page-${page.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-[#F4A261]" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CmsPages;
